import * as React from "react";
import { useState, useRef, useEffect, useCallback } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "leaflet.markercluster";
import HelpIcon from "@mui/icons-material/Help";
import {
	MapContainer,
	TileLayer,
	useMap,
	Marker,
	Popup,
	Tooltip,
} from "react-leaflet";
import { useQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { useSearchParams } from "react-router-dom";

import Driver from "./Driver";
import MapLegend from "./MapLegend";
// import { GET_VANS } from "../graphql/vans";
// import { GET_COORDINATES } from "../graphql/vans";
import { ACTIVE_DRIVERS } from "../graphql/users";

const orderStatusTranslationConformity = {
	CREATED: "1.СТВОРЕНИЙ",
	SUBMITTED_FOR_ASSEMBLY: "2.ПРИЙНЯТИЙ СКЛАДОМ",
	ASSEMBLY_STARTED: "3.СКЛАДАННЯ РОЗПОЧАТО",
	ASSEMBLY_FINISHED: "4.ЗБІРКА ЗАВЕРШЕНА",
	ON_A_WAY: "В ДОРОЗІ",
	DELIVERED: "ДОСТАВЛЕНИЙ",
	UNDELIVERED: "НЕДОСТАВЛЕНИЙ",
};

export default function MapPage() {
	const {
		loading,
		error,
		data: drivers,
		startPolling,
		stopPolling,
	} = useQuery(ACTIVE_DRIVERS, {
		pollInterval: 10000,
		fetchPolicy: "network-only",
	});
	const [map, setMap] = useState(null);
	useEffect(() => {
		if (map) {
			L.control
				.zoom({
					position: "topright",
				})
				.addTo(map);
		}
	}, [map]);

	useEffect(() => {
		startPolling(10000);
		return () => stopPolling();
	}, [startPolling, stopPolling]);

	const [searchParams, setSearchParams] = useSearchParams();
	if (loading)
		return (
			<CircularProgress
				size={24}
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
				}}
			/>
		);

	const driverIdParam = searchParams.get("driverid");
	const driverParam = drivers.activeDrivers.find((d) => d.id == driverIdParam);
	// TODO: set warehouse
	const coordsParam = driverParam
		? [driverParam.car.coords.lat, driverParam.car.coords.lan]
		: [50.34758, 30.454526];

	const setCurrentDriverId = (value) => {
		const coords = drivers.activeDrivers.find((d) => d.id == value).car.coords;
		map.setView([coords.lat, coords.lan], 13);
		// map.setView([49.347797, 30.454547]);
		setSearchParams((params) => {
			params.set("driverid", value);
			return params;
		});
	};

	return (
		<>
			{!loading && (
				<Box sx={{ position: "relative", width: "100%", height: "100vh" }}>
					<MapLegend />
					<Box
						sx={{
							position: "absolute",
							top: "1rem",
							left: "50%",
							transform: "translateX(-50%)",
							textAlign: "center",
							zIndex: 11,
						}}
					>
						<Button
							variant="contained"
							onClick={() =>
								setSearchParams((params) => {
									params.set("driverid", "");
									return params;
								})
							}
						>
							Відмінити вибір машини
						</Button>
						<Box
							sx={{
								margin: "5px",
								padding: "5px",
								background: "#dcdcdd",
								textAlign: "center",
								fontSize: "10px",
							}}
						>
							Обраний автомобіль може "налізати" поверх інших.
							<br /> Ви можете відмінити вибір.
						</Box>
					</Box>
					<Box sx={{ position: "absolute", zIndex: 10 }}>
						{drivers.activeDrivers.map((driver) => (
							<Driver
								key={driver.id}
								driver={driver}
								showDriverLocation={setCurrentDriverId}
							/>
						))}
					</Box>

					<MapContainer
						center={coordsParam}
						zoom={13}
						maxZoom={18}
						zoomControl={false}
						scrollWheelZoom={true}
						ref={setMap}
						style={{
							width: "100%",
							height: "100vh",
							zIndex: 1,
						}}
					>
						<TileLayer
							attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
							url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
						/>
						<VanMarkers
							drivers={drivers.activeDrivers}
							selectedDriverId={driverIdParam}
						/>
					</MapContainer>
				</Box>
			)}
			{loading && (
				<CircularProgress
					size={24}
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
					}}
				/>
			)}
		</>
	);
}

const CustomMarker = ({ driverId, position, text, map }) => {
	const [refReady, setRefReady] = useState(false);
	let popupRef = useRef();

	const [searchParams, setSearchParams] = useSearchParams();
	const isActive = driverId == searchParams.get("driverid");

	useEffect(() => {
		if (refReady && isActive) {
			popupRef.openOn(map);
		}
	}, [driverId, refReady, map, isActive]);

	const myIcon = L.icon({
		iconUrl:
			"data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAXNSR0IArs4c6QAAAIRlWElmTU0AKgAAAAgABQESAAMAAAABAAEAAAEaAAUAAAABAAAASgEbAAUAAAABAAAAUgEoAAMAAAABAAIAAIdpAAQAAAABAAAAWgAAAAAAAABIAAAAAQAAAEgAAAABAAOgAQADAAAAAQABAACgAgAEAAAAAQAAACCgAwAEAAAAAQAAACAAAAAAX7wP8AAAAAlwSFlzAAALEwAACxMBAJqcGAAAAVlpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IlhNUCBDb3JlIDYuMC4wIj4KICAgPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4KICAgICAgPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIKICAgICAgICAgICAgeG1sbnM6dGlmZj0iaHR0cDovL25zLmFkb2JlLmNvbS90aWZmLzEuMC8iPgogICAgICAgICA8dGlmZjpPcmllbnRhdGlvbj4xPC90aWZmOk9yaWVudGF0aW9uPgogICAgICA8L3JkZjpEZXNjcmlwdGlvbj4KICAgPC9yZGY6UkRGPgo8L3g6eG1wbWV0YT4KGV7hBwAAAitJREFUWAntlCtvFFEUgJdnaSDBIlBNWkPAgMAgSBFVTRBVLQaJq0LhcSTIChQGDAkhAdHAH8CRNKgKEkRFgwFSXoXvm73ncrO7M9uZLcHsSb65j3PPY865M73eVKYVmFbgP1fgSBH/GPNyXagmmu5jLY1ytFE7ubLWv2+s0gyvwB04A7+hq/zCUHuZhXuwBVZY3UgxeBgd9vgO31GB2hY/Sgl8YfzRge/YWMVPcAtuwhI8BF9oA5Tj/WH4+ZQtDxq8SwUMrt0unIBSNlmoW0ubp0plzJ8w+VcJGON98r/gArElVVu8GMoKXADfJPrFtJXYXy/ZR5iDi3AJzsNXuAaLYDIfwLM51qQVOEjbyva+JfhZ+JuBi0OSn/gZxMp6AR334DLchfpbqbKj1N50/Fny0M/rP/fAxQixT9JVmuytRs5mMIA9VeKSxrr2R9I/np9xfqx9lKPKBnN7p1EE2kwub6RRx+MqMs7eGMaNmJXrFzx1HgEc1ytN/+G81MXZUaMJHsTemPlNDXAd/KX6G/0M59KaoXcSduA0PIdv4P0xkGLFfKMZWIYm+2fo9fcGHkBOwrmyDTq+6iKJc/fUjZM29tHq6m3iH32fCBHsNnMJp+oUz9rHkrb2Q19gZKPTl2ASJa9Yx62Os2xlib1W9mEUXsxqPy1WGRfT/DXj4zQvz6StPJS6LvaVo8GksncmTbo413RmSDe0EV4YLWUpfr9tZFL7NrGmZ7tX4A/+a+x1rVUR4gAAAABJRU5ErkJggg==",
	});

	return (
		<Marker position={position} icon={myIcon}>
			<Popup
				ref={(r) => {
					popupRef = r;
					setRefReady(true);
				}}
			>
				{text}
			</Popup>
		</Marker>
	);
};

export function VanMarkers({ drivers, selectedDriverId }) {
	const map = useMap();
	const [icons, setIcons] = useState({});
	const myIcon = L.icon({
		iconUrl:
			"data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAXNSR0IArs4c6QAAAIRlWElmTU0AKgAAAAgABQESAAMAAAABAAEAAAEaAAUAAAABAAAASgEbAAUAAAABAAAAUgEoAAMAAAABAAIAAIdpAAQAAAABAAAAWgAAAAAAAABIAAAAAQAAAEgAAAABAAOgAQADAAAAAQABAACgAgAEAAAAAQAAACCgAwAEAAAAAQAAACAAAAAAX7wP8AAAAAlwSFlzAAALEwAACxMBAJqcGAAAAVlpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IlhNUCBDb3JlIDYuMC4wIj4KICAgPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4KICAgICAgPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIKICAgICAgICAgICAgeG1sbnM6dGlmZj0iaHR0cDovL25zLmFkb2JlLmNvbS90aWZmLzEuMC8iPgogICAgICAgICA8dGlmZjpPcmllbnRhdGlvbj4xPC90aWZmOk9yaWVudGF0aW9uPgogICAgICA8L3JkZjpEZXNjcmlwdGlvbj4KICAgPC9yZGY6UkRGPgo8L3g6eG1wbWV0YT4KGV7hBwAAAitJREFUWAntlCtvFFEUgJdnaSDBIlBNWkPAgMAgSBFVTRBVLQaJq0LhcSTIChQGDAkhAdHAH8CRNKgKEkRFgwFSXoXvm73ncrO7M9uZLcHsSb65j3PPY865M73eVKYVmFbgP1fgSBH/GPNyXagmmu5jLY1ytFE7ubLWv2+s0gyvwB04A7+hq/zCUHuZhXuwBVZY3UgxeBgd9vgO31GB2hY/Sgl8YfzRge/YWMVPcAtuwhI8BF9oA5Tj/WH4+ZQtDxq8SwUMrt0unIBSNlmoW0ubp0plzJ8w+VcJGON98r/gArElVVu8GMoKXADfJPrFtJXYXy/ZR5iDi3AJzsNXuAaLYDIfwLM51qQVOEjbyva+JfhZ+JuBi0OSn/gZxMp6AR334DLchfpbqbKj1N50/Fny0M/rP/fAxQixT9JVmuytRs5mMIA9VeKSxrr2R9I/np9xfqx9lKPKBnN7p1EE2kwub6RRx+MqMs7eGMaNmJXrFzx1HgEc1ytN/+G81MXZUaMJHsTemPlNDXAd/KX6G/0M59KaoXcSduA0PIdv4P0xkGLFfKMZWIYm+2fo9fcGHkBOwrmyDTq+6iKJc/fUjZM29tHq6m3iH32fCBHsNnMJp+oUz9rHkrb2Q19gZKPTl2ASJa9Yx62Os2xlib1W9mEUXsxqPy1WGRfT/DXj4zQvz6StPJS6LvaVo8GksncmTbo413RmSDe0EV4YLWUpfr9tZFL7NrGmZ7tX4A/+a+x1rVUR4gAAAABJRU5ErkJggg==",
	});

	const selectedIcon = L.icon({
		iconUrl:
			"data:image/png;base64,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",
	});

	const driverPopUpDisplayObj = (driver) => {
		const clients = [...new Set(driver.orders.map((o) => o.clientName))];
		const clientOrders = (client) =>
			driver.orders.filter((o) => o.clientName === client);
		const displayObj =
			`<b>${driver.car.model} ${driver.car.number}</b><br />` +
			clients
				.map(
					(client) =>
						`<b>${client}</b><br />` +
						clientOrders(client)
							.map(
								(order) =>
									`${order.orderNumber} ${
										orderStatusTranslationConformity[order.status]
									}`
							)
							.join("<br />")
				)
				.join("<br />");
		return displayObj;
	};

	useEffect(() => {
		console.log("vanmarkers useEffect", map);
		const markers = L.markerClusterGroup({
			spiderfyOnMaxZoom: true,
			showCoverageOnHover: true,
			zoomToBoundsOnClick: true,
			removeOutsideVisibleBounds: false,
			// maxClusterRadius: 50,
		});

		drivers
			.filter((d) => d.id !== selectedDriverId)
			.forEach((driver) => {
				// const icon = icons[driver.id];
				// if (icon) {
				const marker = L.marker(
					[driver.car.coords.lat, driver.car.coords.lan],
					{
						icon: myIcon,
					}
				).bindPopup(driverPopUpDisplayObj(driver));
				markers.addLayer(marker);
				// }
			});

		map.addLayer(markers);

		let selectedMarker = null;
		if (selectedDriverId) {
			const driver = drivers.find((x) => x.id === selectedDriverId);
			if (driver) {
				selectedMarker = L.marker(
					[driver.car.coords.lat, driver.car.coords.lan],
					{
						icon: selectedIcon,
					}
				).bindPopup(driverPopUpDisplayObj(driver));
				selectedMarker.setZIndexOffset(300);
				map.addLayer(selectedMarker);
			}
		}

		return () => {
			map.removeLayer(markers);
			if (selectedMarker) map.removeLayer(selectedMarker);
		};
	}, [drivers, map, myIcon, selectedDriverId]);

	const handleImageGenerated = useCallback((driverId, base64Image) => {
		setIcons((prevIcons) => ({
			...prevIcons,
			[driverId]: L.icon({
				iconUrl: base64Image,
				iconSize: [300, 48],
				iconAnchor: [150, 24],
			}),
		}));
	}, []);
	return (
		<>
			{/* {drivers.map((driver) => (
				<IconWithText
					key={driver.id}
					text={driver.car.name}
					onImageGenerated={(base64Image) =>
						handleImageGenerated(driver.id, base64Image)
					}
				/>
			))} */}
		</>
	);
}
